/**
 * Smart Log Analyzer Engine for FortiOS Syslog Telemetry
 * Parses raw FortiGate syslog telemetry and generates human-readable narratives,
 * contextual threat assessments, and forensic summaries without generic placeholders.
 */

export const analyzeLog = (log = {}) => {
    // 1. Normalize Core Fields
    const severity = (log.severity || log.level || 'info').toLowerCase();
    const action = (log.action || '').toLowerCase();
    const subtype = (log.subtype || '').toLowerCase();
    const type = (log.type || '').toLowerCase();
    const devname = log.devname || log.deviceName || 'Perimeter Gateway';
    const policyname = log.policyname || (log.policyid ? `Policy #${log.policyid}` : null);
    const service = log.service || log.app || log.dstinetsvc || '';
    const app = log.app || '';
    const attack = log.attack || '';
    const msg = log.msg || log.logdesc || log.humanMessage || '';

    // Source & Destination IP normalization
    const srcRaw = log.srcip || log.sourceIp || log.remip || log.client_ip;
    const src = (srcRaw && srcRaw !== 'Unknown Source' && srcRaw !== '-') ? srcRaw : (log.mastersrcmac || 'Internal Host');

    const dstRaw = log.dstip || log.destIp || log.locip || log.dst_host || log.hostname;
    const dst = (dstRaw && dstRaw !== 'Unknown Destination' && dstRaw !== '-') ? dstRaw : (log.dstinetsvc || 'External Endpoint');

    const srcPort = log.srcport || log.sourcePort;
    const dstPort = log.dstport || log.destPort;
    const srcIntf = log.srcintf || log.srcintfrole;
    const dstIntf = log.dstintf || log.dstintfrole;

    // Byte conversion helper
    const sentBytes = parseInt(log.sentbyte || 0);
    const rcvdBytes = parseInt(log.rcvdbyte || 0);
    const totalBytes = sentBytes + rcvdBytes;

    let byteStr = '';
    if (totalBytes > 1024 * 1024 * 1024) {
        byteStr = ` (${(totalBytes / (1024 * 1024 * 1024)).toFixed(2)} GB transferred)`;
    } else if (totalBytes > 1024 * 1024) {
        byteStr = ` (${(totalBytes / (1024 * 1024)).toFixed(2)} MB transferred)`;
    } else if (totalBytes > 1024) {
        byteStr = ` (${(totalBytes / 1024).toFixed(1)} KB transferred)`;
    }

    // 2. Determine Risk & Urgency
    let riskScore = 10;
    let riskLevel = 'Routine Telemetry';
    let riskColor = 'text-emerald-400';
    let urgency = 'No Action Required';

    const isBlocked = action.includes('block') || action.includes('deny') || action.includes('drop') || action.includes('blocked');

    if (severity.includes('crit') || severity.includes('alert') || attack) {
        riskScore = 95;
        riskLevel = 'Critical Incident';
        riskColor = 'text-rose-500 animate-pulse font-black';
        urgency = isBlocked ? 'Threat Blocked (Review Alert)' : 'Immediate Threat Mitigation Required';
    } else if (severity.includes('error') || severity.includes('err')) {
        riskScore = 75;
        riskLevel = 'High Priority';
        riskColor = 'text-rose-400 font-bold';
        urgency = 'Investigate Network Failure / Anomaly';
    } else if (severity.includes('warn')) {
        riskScore = 50;
        riskLevel = 'Security Warning';
        riskColor = 'text-amber-400 font-bold';
        urgency = isBlocked ? 'Security Rule Enforced' : 'Monitor Connection Pattern';
    } else if (isBlocked) {
        riskScore = 40;
        riskLevel = 'Policy Enforced';
        riskColor = 'text-amber-300';
        urgency = 'Blocked by Security Perimeter';
    } else {
        riskScore = 10;
        riskLevel = 'Normal Operation';
        riskColor = 'text-emerald-400';
        urgency = 'Standard Traffic Flow';
    }

    // 3. Construct Narrative (What / Why)
    let what = '';
    let why = '';

    if (attack) {
        what = `IPS Signature Triggered: ${attack} targeting ${dst} from ${src}.`;
        why = isBlocked 
            ? `FortiGate IPS engine matched an exploit signature and successfully terminated the session.`
            : `Intrusion detection matched active threat patterns. Immediate perimeter investigation recommended.`;
    } else if (subtype === 'file-filter' || msg.toLowerCase().includes('file')) {
        what = `Perimeter File Filter: Transfer blocked between ${src} and ${dst}.`;
        why = msg || `A prohibited file extension or content pattern was intercepted according to organizational policy.`;
    } else if (subtype === 'virus' || type === 'virus') {
        what = `Antivirus Shield: Malicious payload intercepted from ${src}.`;
        why = `FortiGuard AV signatures detected known malware in packet stream.`;
    } else if (subtype === 'webfilter' || subtype === 'urlfilter') {
        what = `Web Filter Policy: Access to ${dst} ${isBlocked ? 'blocked' : 'monitored'}.`;
        why = `Destination matched restricted web filter category on gateway ${devname}.`;
    } else if (subtype === 'ssl' || action.includes('ssl') || service.toLowerCase().includes('ssl')) {
        if (action.includes('fail') || severity.includes('error') || severity.includes('warn')) {
            what = `SSL/TLS Negotiation Issue detected on ${devname} (${src} -> ${dst}).`;
            why = msg || `Certificate validation anomaly, cipher mismatch, or handshake drop during secure negotiation.`;
        } else {
            what = `Encrypted SSL/TLS session established between ${src} and ${dst}${byteStr}.`;
            why = `Legitimate encrypted channel passing through ${devname} inspection profile.`;
        }
    } else if (subtype === 'vpn' || action.includes('tunnel') || action.includes('ipsec') || action.includes('phase1')) {
        if (action.includes('fail') || action.includes('down') || severity.includes('error')) {
            what = `VPN Gateway event: ${action.toUpperCase()} on ${devname} for peer ${src}.`;
            why = msg || `IKE Phase negotiation timeout or pre-shared key/certificate authentication failure.`;
        } else {
            what = `VPN Gateway active tunnel: ${action.toUpperCase()} on ${devname} with ${src}.`;
            why = `Secure encrypted tunnel operating normally.`;
        }
    } else if (subtype === 'dns' || service.toLowerCase() === 'dns') {
        what = `DNS Query relayed from ${src} to nameserver ${dst}.`;
        why = `Standard domain name resolution traffic routed via ${devname}.`;
    } else if (subtype === 'endpoint' || type === 'event') {
        what = `Endpoint & System Event: ${log.logdesc || msg || 'Device Status Notification'}.`;
        why = `Host ${src} registered telemetry state update with gateway ${devname}.`;
    } else if (action === 'accept' || action === 'pass') {
        const appLabel = app || service || 'network';
        const polStr = policyname ? ` under rule "${policyname}"` : '';
        what = `Permitted ${appLabel} traffic from ${src} to ${dst}${polStr}${byteStr}.`;
        why = `Traffic fully conformed to firewall access control policy on interface ${srcIntf || 'lan'} -> ${dstIntf || 'wan'}.`;
    } else if (isBlocked) {
        const appLabel = app || service || 'traffic';
        const polStr = policyname ? ` (Policy: ${policyname})` : '';
        what = `Firewall blocked unauthorized ${appLabel} connection from ${src} to ${dst}${polStr}.`;
        why = `No matching permit policy or explicit perimeter DENY rule triggered on ${devname}.`;
    } else if (action === 'close' || action === 'client-rst' || action === 'server-rst') {
        const appLabel = app || service || 'session';
        what = `Session gracefully closed (${action}) for ${appLabel} between ${src} and ${dst}${byteStr}.`;
        why = `Normal TCP/UDP session teardown initiated by ${action.includes('client') ? 'client' : action.includes('server') ? 'server' : 'gateway'}.`;
    } else if (action === 'timeout') {
        what = `Session timed out for ${app || service || 'connection'} between ${src} and ${dst}.`;
        why = `No keep-alive packets received within policy idle timeout threshold.`;
    } else {
        what = msg || `FortiOS Telemetry Event: ${service || subtype || 'Traffic'} activity on ${devname}.`;
        why = `Logged in accordance with firewall system reporting parameters.`;
    }

    return {
        what,
        why,
        isThreat: riskLevel,
        action: urgency,
        riskColor,
        riskScore
    };
};
